import { SavedSearch } from '@creditsights/api-ui-web';
import { Button, Icon, ICONS } from '@creditsights/styleguide-web';
import { useLocation, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames/bind';
import { useEffect, useRef, useState } from 'react';
import { ROUTES } from '../../../Base.constants';
import useFetchRecentSearches from '../../../hooks/preferences/useFetchRecentSearches';
import useUpdateRecentSearches from '../../../hooks/preferences/useUpdateRecentSearches';
import useSearchDropdownListeners from '../../../hooks/searchBar/useSearchDropdownListeners';
import useSearchBarKeyboardListeners from '../../../hooks/searchBar/useSearchBarKeyboardListener';
import { SEARCH_TERM_MAX_LENGTH } from '../../../utils/searchSuggestUtils';
import { getSavedSearchQueryParams } from './SearchBar.logic';
import SearchSuggest from './SearchSuggest';
import styles from './SearchBar.module.scss';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import useFetchUserProfile from '../../../hooks/profile/useFetchUserProfile';
import { trackSearchPhrase } from '../../../utils/navigationUtils';
import { SearchSuggestItem } from '../../../models/SearchSuggest';
import CloseIcon from './CloseIcon';

const cx = classNames.bind(styles);

interface SearchBarProps {
  csGeniePermission: Boolean;
}

const SearchBar = ({ csGeniePermission }: SearchBarProps): React.ReactElement => {
  const [isFocused, setIsFocused] = useState<Boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typedSearchTerm, setTypedSearchTerm] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTermState = useRef<string>(searchTerm);
  const navigate = useNavigate();
  const { mutate: updateRecentSearches } = useUpdateRecentSearches();
  const { data: recentSearches } = useFetchRecentSearches();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchSuggestVerticalIndex, setSearchSuggestVerticalIndex] = useState<number | null>(null);
  const [tagsSearchList, setTagsSearchList] = useState<SearchSuggestItem[]>([]);
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: profile } = useFetchUserProfile();
  const location = useLocation();
  useEffect(() => {
    clearSearchTermIfNotOnSearchPage();
  }, [navigate]);

  useEffect(() => {
    if (!searchParams.get('tagsList')) {
      setTagsSearchList([]);
    }
  }, [searchParams]);

  useEffect(() => {
    if (typedSearchTerm.length > 0) {
      setIsFocused(true);
    }
  }, [typedSearchTerm]);

  const closeDropdown = () => {
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const setSearchTermState = (searchTerm: string) => {
    searchTermState.current = searchTerm;
    setSearchTerm(searchTerm);
    if (!searchTerm.trim()) {
      setTypedSearchTerm('');
    }
  };

  const handleFocus = () => {
    setTypedSearchTerm(searchTermState.current);
    setIsFocused(true);
  };

  const handleChange = ({ target }) => {
    setSearchTermState(target.value);
    setTypedSearchTerm(target.value);
  };

  const clearSearch = () => {
    setSearchTermState('');
    setTypedSearchTerm('');
  };

  const handleClearSearchClick = () => {
    clearSearch();
    inputRef.current?.focus();
    setIsFocused(false);
  };

  const onRemoveTagBox = (tagItem: SearchSuggestItem) => {
    setTagsSearchList((prev) => {
      const next = [...prev];
      const index = prev.indexOf(tagItem);
      if (index > -1) {
        next.splice(index, 1);
      }

      return next;
    });
  };

  const onRemoveLastElement = () => {
    if (tagsSearchList?.length > 0) {
      const tagItem = tagsSearchList[tagsSearchList.length - 1];
      onRemoveTagBox(tagItem);
    }
  };

  useSearchDropdownListeners({
    closeDropdown,
    dropdownRef,
    inputRef,
    setIsFocused,
    searchSuggestVerticalIndex,
    handleClearSearchClick,
  });

  useSearchBarKeyboardListeners({ inputRef, tagsSearchList, onRemoveLastElement, typedSearchTerm });

  const saveRecentSearch = (searchTerm: string, category: string = 'All') => {
    if (!searchTerm) return;
    if (recentSearches) {
      const recentSearchValues = [...recentSearches.value];
      const existingRecentSearch = recentSearchValues.find((recentSearch) => recentSearch.searchTerm === searchTerm);
      if (existingRecentSearch) {
        existingRecentSearch.searchedAt = new Date().toISOString();
      } else {
        recentSearchValues.push({ searchTerm, category, searchedAt: new Date().toISOString() });
      }
      updateRecentSearches({ id: recentSearches?.id, value: recentSearchValues });
    }
  };
  const isSearchPage = () => {
    return location.pathname === ROUTES.search || location.pathname.startsWith(`${ROUTES.search}?`);
  };
  const clearSearchTermIfNotOnSearchPage = () => {
    closeDropdown();
    if (!isSearchPage()) {
      setSearchTermState('');
    }
  };

  const buildTagsParams = () => {
    return tagsSearchList.reduce((acc, curr) => {
      if (acc === '') {
        return curr.id;
      }
      return `${acc}+${curr.id}`;
    }, '');
  };

  const submitSearch = (searchTerm: string, category: string = 'All') => {
    trackSearchPhrase(searchTerm, category);
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm || tagsSearchList.length > 0) {
      saveRecentSearch(searchTerm, category);
      if (isSearchPage()) {
        searchParams.set('searchTerm', trimmedSearchTerm);
        searchParams.set('category', category);
        searchParams.delete('tagsList');
        if (category === 'Topics' && tagsSearchList.length > 0) {
          searchParams.set('tagsList', buildTagsParams());
        }
        setSearchParams(searchParams);
      } else {
        const navigateParams = createSearchParams({
          searchTerm: trimmedSearchTerm,
          category,
        });
        if (tagsSearchList.length > 0) navigateParams.append('tagsList', buildTagsParams());
        navigate({
          pathname: ROUTES.search,
          search: navigateParams.toString(),
        });
      }
      closeDropdown();
    }
  };

  const navigateToLink = (route: string) => {
    navigate(route);
    setSearchTermState('');
    closeDropdown();
  };

  const navigateToSavedSearch = (savedSearch: SavedSearch) => {
    navigate({
      pathname: ROUTES.search,
      search: getSavedSearchQueryParams(savedSearch),
    });
    closeDropdown();
  };

  const handleKeyDown = ({ key }) => {
    if (key === 'Enter' && searchSuggestVerticalIndex === null) {
      if (tagsSearchList.length > 0) {
        submitSearch(searchTerm, 'Topics');
      } else {
        submitSearch(searchTerm);
      }
    }
    if (key === 'Escape') {
      inputRef.current?.blur();
    }
  };

  const renderTagBox = (tagItem: SearchSuggestItem) => {
    return (
      <div className={cx('tag-box')} key={tagItem.id}>
        {tagItem.displayName}{' '}
        <button
          type="button"
          onClick={() => {
            onRemoveTagBox(tagItem);
          }}
          className={cx('tag-box-remove')}
          data-testid="close-button"
        >
          <CloseIcon />
        </button>
      </div>
    );
  };

  const onAddTags = (suggestTag: SearchSuggestItem) => {
    setTagsSearchList((prev) => {
      const tagExists = prev.find((item) => item.id === suggestTag.id);
      const next = [...prev];
      if (!tagExists) {
        next.push(suggestTag);
      }

      return next;
    });
  };

  const searchSuggestCheck = () => {
    if (tagsSearchList.length > 0) {
      return isFocused && typedSearchTerm;
    }
    return isFocused;
  };

  return (
    <div className={cx('body', { csGeniePermission })}>
      {!!userPermissions && (
        <div
          className={cx('search-bar', { focused: isFocused }, { disabled: !profile?.active })}
          data-testid="search-bar"
        >
          <div className={cx('search-icon')} data-testid="search-icon" aria-hidden>
            <Icon name={ICONS.Search} height={12} width={12} />
          </div>
          <div id="tag-box-parent" className={cx('tag-box-parent')}>
            {tagsSearchList.map((tagItem) => renderTagBox(tagItem))}
            <input
              autoComplete="off"
              aria-label="Search by keyword or phrase"
              className={cx('search-input')}
              maxLength={SEARCH_TERM_MAX_LENGTH}
              onChange={handleChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder="Search by keyword or phrase"
              ref={inputRef}
              value={searchTerm}
              id="search-input"
            />
          </div>
          {searchTerm.length > 0 && (
            <Button
              ariaLabel="clear input"
              className={cx('clear-button')}
              dataTestId="clear-button"
              id="clear-button"
              onClick={handleClearSearchClick}
            >
              <Icon name={ICONS.Clear} width={12} height={12} />
            </Button>
          )}
        </div>
      )}
      {searchSuggestCheck() && (
        <SearchSuggest
          dropdownRef={dropdownRef}
          term={typedSearchTerm}
          onSuggestionSelect={setSearchTermState}
          onSuggestionClick={submitSearch}
          submitSearch={submitSearch}
          navigateToSavedSearch={navigateToSavedSearch}
          onLinkClick={navigateToLink}
          verticalIndex={searchSuggestVerticalIndex}
          setVerticalIndex={setSearchSuggestVerticalIndex}
          tagsSearchList={tagsSearchList}
          handleClearSearchClick={handleClearSearchClick}
          onAddTags={onAddTags}
        />
      )}
    </div>
  );
};

export default SearchBar;
